import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import { filterLastNMonths, groupSumByMonth } from '@/lib/dashboardAggregations';

const TREND_MONTHS = 6;
import { withId } from '@/pages/reports/reportHelpers';
import { usePortalCategoryColors } from '../portalColors';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from '../PortalDateRangeFilter';
import type { GrnRegisterRow } from '@/types/reports';

export default function GrnPanel() {
  const { t } = useTranslation('clientPortal');
  const colors = usePortalCategoryColors();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/client-portal/grn-register')).data,
  });
  // Date-range filter only narrows the table below — the stat cards/trend chart above keep
  // summarizing the full register (the chart already has its own 6-month window).
  const tableRows = useMemo(() => rows.filter((r) => isWithinDateRange(r.grnDate, fromDate, toDate)), [rows, fromDate, toDate]);

  const totalGrns = useMemo(() => new Set(rows.map((r) => r.grnId)).size, [rows]);
  const acceptedQty = useMemo(() => rows.reduce((sum, r) => sum + r.acceptedQty, 0), [rows]);
  const rejectedQty = useMemo(() => rows.reduce((sum, r) => sum + r.rejectedQty, 0), [rows]);

  const monthlyTrend = useMemo(
    () => groupSumByMonth(filterLastNMonths(rows, (r) => r.grnDate, TREND_MONTHS), (r) => r.grnDate, [
      { key: 'accepted', value: (r) => r.acceptedQty },
      { key: 'rejected', value: (r) => r.rejectedQty },
    ]),
    [rows],
  );

  const columns: DataTableColumn<GrnRegisterRow>[] = [
    { field: 'grnNumber', headerName: t('grnPage.table.grnNumber') },
    { field: 'grnDate', headerName: t('grnPage.table.grnDate'), render: (r) => new Date(r.grnDate).toLocaleDateString() },
    { field: 'supplierName', headerName: t('grnPage.table.supplierName'), filterable: true },
    { field: 'materialName', headerName: t('grnPage.table.materialName'), filterable: true },
    { field: 'acceptedQty', headerName: t('grnPage.table.acceptedQty'), align: 'right' },
    { field: 'status', headerName: t('grnPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('grnPanel.totalGrns')} value={totalGrns} icon={<MoveToInboxIcon />} accentColor={colors.grn} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('grnPanel.acceptedQty')} value={acceptedQty.toLocaleString()} icon={<CheckCircleOutlineIcon />} accentColor={colors.overview} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('grnPanel.rejectedQty')} value={rejectedQty.toLocaleString()} icon={<CancelOutlinedIcon />} accentColor={colors.invoices} />
        </GridItem>
      </GridContainer>

      <ChartCard title={t('grnPanel.trendTitle')} loading={isLoading} empty={!isLoading && monthlyTrend.length === 0} height={280}>
        <DashboardLineChart
          categories={monthlyTrend.map((m) => m.month)}
          series={[
            { label: t('grnPage.table.acceptedQty'), data: monthlyTrend.map((m) => m.values.accepted), color: colors.overview },
            { label: t('grnPage.table.rejectedQty'), data: monthlyTrend.map((m) => m.values.rejected), color: colors.invoices },
          ]}
          height={280}
          legendPosition="bottom"
          showValueLabels
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('grnPage.title')}
        </Typography>
        <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
        <DataTable
          columns={columns}
          rows={withId(tableRows, (r) => `${r.grnId}-${r.materialCode}`)}
          loading={isLoading}
          searchable
          exportFileName="grn-register"
        />
      </Paper>
    </Stack>
  );
}
